import React, { useContext, useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SortOrder } from 'primereact/api';
import { getEntities } from 'app/api/generalApi';
import { ToastContext } from 'app/context/ToastContext';
import { formatBigDecimalIntoString } from 'app/shared/util/number-utils';
import { boldNameFormatter } from 'app/shared/util/formatters';

const WaitingProvisionAgentTable: React.FC = () => {
  const { showError } = useContext(ToastContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [expandedRows, setExpandedRows] = useState(null);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 20,
    page: 0,
    sortField: 'id',
    sortOrder: SortOrder.DESC,
  });

  const loadData = () => {
    setLoading(true);
    const sort = lazyParams.sortField + ',' + (lazyParams.sortOrder === SortOrder.DESC ? 'desc' : 'asc');
    getEntities('/api/waiting-provision-agents', { page: lazyParams.page, size: lazyParams.rows, sort })
      .then(response => {
        setItems(response.data.content);
        setTotalRecords(response.data.totalElements);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [lazyParams]);

  const allowExpansion = rowData => rowData.agentProvisions && rowData.agentProvisions.length > 0;

  const isNegativeRow = ap => ap.isStorno || (Number(ap.provision) || 0) < 0;

  const sumaBody = ap => (
    <span style={{ color: isNegativeRow(ap) ? '#e31818' : '#16a34a', fontWeight: 600 }}>
      {formatBigDecimalIntoString(ap.provision)} EUR
    </span>
  );

  const expandTemplate = rowData => {
    const provs = rowData.agentProvisions || [];
    let totalProvizie = 0;
    let totalStorno = 0;
    for (const ap of provs) {
      const val = Number(ap.provision) || 0;
      if (isNegativeRow(ap)) totalStorno += val;
      else totalProvizie += val;
    }
    const net = totalProvizie + totalStorno;

    const footer = (
      <div style={{ textAlign: 'right', lineHeight: 1.6 }}>
        <div>
          Spolu provízie:{' '}
          <span style={{ color: '#16a34a', fontWeight: 700 }}>{formatBigDecimalIntoString(totalProvizie.toFixed(2))} EUR</span>
        </div>
        <div>
          Spolu storno:{' '}
          <span style={{ color: '#e31818', fontWeight: 700 }}>{formatBigDecimalIntoString(totalStorno.toFixed(2))} EUR</span>
        </div>
        <div>
          Netto:{' '}
          <span style={{ color: net < 0 ? '#e31818' : '#16a34a', fontWeight: 700 }}>
            {formatBigDecimalIntoString(net.toFixed(2))} EUR
          </span>
        </div>
      </div>
    );

    return (
      <div className="p-3">
        <h5>Zoznam zmlúv</h5>
        <DataTable value={provs} dataKey="id" size="small" stripedRows footer={footer} emptyMessage="Žiadne zmluvy.">
          <Column field="proposalNr" header="Číslo návrhu" />
          <Column field="client" header="Klient" />
          <Column field="agentName" header="Obchodník" />
          <Column field="insuranceCompany" header="Poisťovňa, produkt" />
          <Column field="isStorno" header="Typ" body={v => (isNegativeRow(v) ? 'storno' : 'provízia')} />
          <Column field="provision" header="Suma" body={sumaBody} />
        </DataTable>
      </div>
    );
  };

  return (
    <div>
      <div className="card">
        <DataTable
          stripedRows
          value={items}
          size="small"
          lazy
          responsiveLayout="scroll"
          dataKey="id"
          emptyMessage="Žiadne dáta."
          paginator
          first={lazyParams.first}
          rows={20}
          totalRecords={totalRecords}
          onPage={e => setLazyParams({ ...lazyParams, first: e.first, page: e.page, rows: e.rows })}
          onSort={e => setLazyParams({ ...lazyParams, sortField: e.sortField, sortOrder: e.sortOrder })}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          rowExpansionTemplate={expandTemplate}
          expandedRows={expandedRows}
          onRowToggle={e => setExpandedRows(e.data)}
          loading={loading}
        >
          <Column expander={allowExpansion} style={{ width: '3rem' }} />
          <Column field="id" header="ID" sortable />
          <Column
            field="agent.nameFullFormatted"
            header="Meno poradcu"
            body={v => boldNameFormatter(v?.agent?.nameFullFormatted)}
          />
          <Column
            field="provision"
            header="Výška provízie"
            sortable
            body={v => `${formatBigDecimalIntoString(v.provision)} EUR`}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default WaitingProvisionAgentTable;
